/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { FaSave, FaUndo } from "react-icons/fa";
import { v4 as uuidv4 } from "uuid";
import AdminSearchableSidebar from "../../../Components/AdminComponents/SampleDataEdit/AdminSearchableSidebar";
// import { DataSampleSections } from "../../../Components/SampleDataPage/mockData";
import LoadingSpinner from "../../../LoadingSpinner";
import apiClient from "../../../utils/ApiClient/apiClientAxios";
import AdminContentPanel from "./Admincontentpanel";
import AdminSectionCardsPanel from "./AdminSectionCardsPanel";
import DeleteConfirmModal from "./DeleteConfirmModal";
import ItemFormModal from "./Itemformmodal";
import SectionFormModal from "./Sectionformmodal";
const deepClone = (data) => JSON.parse(JSON.stringify(data));

const normalizeSections = (sections) => {
  return sections.map((section, index) => ({
    ...section,
    id: section.id || uuidv4(),
    title: section.title,
    items: Array.isArray(section.items) ? section.items : [],
  }));
};

const AdminHealthTopicsLayout = () => {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });
  const toast = useToast();

  const [healthSections, setHealthSections] = useState([]);
  const [originalHealthSections, setOriginalHealthSections] = useState([]);

  /** UI STATE (IDs ONLY) */
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [activeItemId, setActiveItemId] = useState(null);
  const [viewMode, setViewMode] = useState("cards");

  /** FLAGS */
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  /** MODALS */
  const sectionModal = useDisclosure();
  const itemModal = useDisclosure();
  const deleteModal = useDisclosure();

  const [editingSection, setEditingSection] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  /** DERIVED DATA - Always computed from current state */
  const activeSection = useMemo(
    () => healthSections.find((s) => s.id === activeSectionId),
    [healthSections, activeSectionId],
  );

  const activeItem = useMemo(
    () => activeSection?.items?.find((i) => i.id === activeItemId),
    [activeSection, activeItemId],
  );

  /** FETCH INITIAL DATA */
  useEffect(() => {
    fetchHealthSections();
  }, []);

  const fetchHealthSections = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/getData");
      let sectionsData = data;
      // if (sectionsData.length === 0) {
      //   sectionsData = DataSampleSections || [];
      // }
      sectionsData = normalizeSections(sectionsData);
      setHealthSections(deepClone(sectionsData));
      setOriginalHealthSections(deepClone(sectionsData));

      if (sectionsData.length > 0) {
        setActiveSectionId(sectionsData[0].id);
      }
    } catch (error) {
      console.error("Error fetching sections:", error);

      // let fallbackData = DataSampleSections || [];
      // fallbackData = normalizeSections(fallbackData);

      // setHealthSections(fallbackData);
      // setOriginalHealthSections(fallbackData);

      // if (fallbackData.length > 0) {
      //   setActiveSectionId(fallbackData[0].id);
      // }
    } finally {
      setLoading(false);
    }
  };

  /** CHANGE TRACKING - Detects unsaved changes */
  useEffect(() => {
    const hasChanges =
      JSON.stringify(originalHealthSections) !== JSON.stringify(healthSections);
    setHasUnsavedChanges(hasChanges);
  }, [healthSections, originalHealthSections]);

  /** SECTION CRUD OPERATIONS */
  const handleSaveSection = (sectionData) => {
    if (editingSection) {
      // UPDATE existing section - only update title and img
      setHealthSections((prev) =>
        prev.map((section) => {
          if (section.id === editingSection.id) {
            return {
              ...section,
              title: sectionData.title,
              footerImg: sectionData.footerImg,
            };
          }
          return section;
        }),
      );

      toast({
        title: "Section updated",
        description: `"${sectionData.title}" has been updated`,
        status: "success",
        duration: 2000,
      });
    } else {
      // ADD new section
      const newSection = {
        id: uuidv4(),
        title: sectionData.title,
        footerImg: sectionData.footerImg || "",
        items: [],
      };

      setHealthSections((prev) => [...prev, newSection]);

      // Set the new section as active
      setActiveSectionId(newSection.id);
      setViewMode("cards");

      toast({
        title: "Section added",
        description: `"${sectionData.title}" has been created`,
        status: "success",
        duration: 2000,
      });
    }

    setEditingSection(null);
    sectionModal.onClose();
  };

  const handleSaveItem = (itemData) => {
    if (!activeSectionId) {
      toast({
        title: "No section selected",
        description: "Please select a section before adding an item",
        status: "error",
        duration: 3000,
      });
      return;
    }

    setHealthSections((prev) =>
      prev.map((section) => {
        if (section.id !== activeSectionId) {
          return section;
        }

        if (editingItem) {
          // UPDATE existing item
          return {
            ...section,
            items: section.items.map((item) =>
              item.id === editingItem.id ? { ...item, ...itemData } : item,
            ),
          };
        } else {
          // ADD new item
          const newItem = {
            ...itemData,
            id: uuidv4(),
          };

          return {
            ...section,
            items: [...(section.items || []), newItem],
          };
        }
      }),
    );

    setEditingItem(null);
    itemModal.onClose();

    toast({
      title: editingItem ? "Item updated" : "Item added",
      description: `"${itemData.title}" has been ${
        editingItem ? "updated" : "added"
      }`,
      status: "success",
      duration: 2000,
    });
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;

    if (deleteTarget.type === "section") {
      const sectionToDelete = deleteTarget.data;
      const itemCount = sectionToDelete.items?.length || 0;

      setHealthSections((prev) => {
        const filtered = prev.filter((s) => s.id !== sectionToDelete.id);

        if (activeSectionId === sectionToDelete.id) {
          if (filtered.length > 0) {
            setActiveSectionId(filtered[0].id);
          } else {
            setActiveSectionId(null);
          }
        }

        return filtered;
      });

      setActiveItemId(null);
      setViewMode("cards");

      toast({
        title: "Section deleted",
        description: `"${sectionToDelete.title}" and ${itemCount} item(s) deleted`,
        status: "success",
        duration: 2000,
      });
    } else if (deleteTarget.type === "item") {
      const itemToDelete = deleteTarget.data;

      setHealthSections((prev) =>
        prev.map((section) => {
          if (section.id !== activeSectionId) {
            return section;
          }

          return {
            ...section,
            items: section.items.filter((i) => i.id !== itemToDelete.id),
          };
        }),
      );

      if (activeItemId === itemToDelete.id) {
        setActiveItemId(null);
        setViewMode("cards");
      }

      toast({
        title: "Item deleted",
        description: `"${itemToDelete.title}" has been deleted`,
        status: "success",
        duration: 2000,
      });
    }

    setDeleteTarget(null);
    deleteModal.onClose();
  };

  /** SAVE ALL CHANGES TO SERVER */
  const handleSaveAllChanges = async () => {
    setSaving(true);
    const payload = {
      id: "Data",
      TYPE: "DATA",
      CONFIG: healthSections,
    };
    try {
      await apiClient.post("/writeConfig", payload);
      setOriginalHealthSections(deepClone(healthSections));
      toast({
        title: "Saved successfully",
        description: "All changes have been saved to the server",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Save error:", error);
    } finally {
      setSaving(false);
    }
  };

  /** DISCARD ALL CHANGES */
  const handleDiscardChanges = () => {
    if (!hasUnsavedChanges) return;

    setHealthSections(deepClone(originalHealthSections));
    setHasUnsavedChanges(false);

    if (originalHealthSections.length > 0) {
      const stillExists = originalHealthSections.find(
        (s) => s.id === activeSectionId,
      );
      if (!stillExists) {
        setActiveSectionId(originalHealthSections[0].id);
      }
    } else {
      setActiveSectionId(null);
    }

    setActiveItemId(null);
    setViewMode("cards");

    toast({
      title: "Changes discarded",
      description: "All unsaved changes have been reverted",
      status: "info",
      duration: 2000,
    });
  };

  /** EDIT HANDLERS */
  const handleEditSection = (section) => {
    console.log("sec", section);
    setEditingSection(section);
    sectionModal.onOpen();
  };

  const handleEditItem = (item) => {
    if (!activeSectionId) {
      toast({
        title: "No section selected",
        description: "Please select a section first",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    setEditingItem(item);
    itemModal.onOpen();
  };

  /** ADD HANDLERS */
  const handleAddSection = () => {
    setEditingSection(null);
    sectionModal.onOpen();
  };

  const handleAddItem = () => {
    if (!activeSectionId || !activeSection) {
      toast({
        title: "No section selected",
        description: "Please select a section before adding an item",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    setEditingItem(null);
    itemModal.onOpen();
  };

  /** DELETE HANDLERS */
  const handleDeleteSection = (section) => {
    setDeleteTarget({ type: "section", data: section });
    deleteModal.onOpen();
  };

  const handleDeleteItem = (item) => {
    setDeleteTarget({ type: "item", data: item });
    deleteModal.onOpen();
  };

  const handleReorderSections = (updatedSections) => {
    setHealthSections(updatedSections);
  };
  const handleReorderItems = (reorderedItems) => {
    setHealthSections((prev) =>
      prev.map((section) =>
        section.id === activeSectionId
          ? { ...section, items: reorderedItems }
          : section,
      ),
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box minH="100vh" bg="gray.50">
      {/* HEADER */}
      <Box
        bg="white"
        borderBottom="1px"
        borderColor="gray.200"
        py={3}
        boxShadow="sm"
      >
        <Flex mx={5} justify="space-between" align="center">
          <Box></Box>

          {hasUnsavedChanges && (
            <HStack
              spacing={3}
              bg="orange.50"
              px={4}
              py={2}
              borderRadius="md"
              border="1px"
              borderColor="orange.200"
            >
              <Badge colorScheme="orange" fontSize="sm">
                Unsaved Changes
              </Badge>
              <Text fontSize="sm" color="orange.700">
                {healthSections.length !== originalHealthSections.length
                  ? `${Math.abs(
                      healthSections.length - originalHealthSections.length,
                    )} section(s) changed`
                  : "Changes detected"}
              </Text>
              <Button
                size="sm"
                variant="outline"
                colorScheme="orange"
                leftIcon={<FaUndo />}
                onClick={handleDiscardChanges}
              >
                Discard
              </Button>
              <Button
                size="sm"
                colorScheme="teal"
                leftIcon={<FaSave />}
                onClick={handleSaveAllChanges}
                isLoading={saving}
                loadingText="Saving..."
              >
                Save All Changes
              </Button>
            </HStack>
          )}
        </Flex>
      </Box>

      {/* BODY */}
      <Container maxW="1680px" px={0}>
        <HStack align="stretch" spacing={0} minH="calc(100vh - 60px)">
          {viewMode === "cards" ? (
            <AdminSectionCardsPanel
              section={activeSection}
              onItemClick={(item) => {
                setActiveItemId(item.id);
                setViewMode("detail");
              }}
              onAddItem={handleAddItem}
              onEditItem={handleEditItem}
              onDeleteItem={handleDeleteItem}
              onReorder={handleReorderItems}
            />
          ) : (
            <AdminContentPanel
              content={activeItem}
              onBack={() => {
                setViewMode("cards");
                setActiveItemId(null);
              }}
              onEdit={() => {
                handleEditItem(activeItem);
              }}
              onDelete={() => {
                setDeleteTarget({ type: "item", data: activeItem });
                deleteModal.onOpen();
              }}
            />
          )}

          <AdminSearchableSidebar
            sections={healthSections}
            onReorderSections={handleReorderSections}
            activeSectionId={activeSectionId}
            activeItemId={activeItemId}
            onSectionClick={(s) => {
              setActiveSectionId(s.id);
              setActiveItemId(null);
              setViewMode("cards");
            }}
            onItemClick={(i) => {
              setActiveItemId(i.id);
              setViewMode("detail");
            }}
            onAddSection={handleAddSection}
            onEditSection={handleEditSection}
            onDeleteSection={handleDeleteSection}
            isOpen={isOpen}
            onToggle={onToggle}
          />
        </HStack>
      </Container>

      {/* MODALS */}
      <SectionFormModal
        isOpen={sectionModal.isOpen}
        onClose={() => {
          sectionModal.onClose();
          setEditingSection(null);
        }}
        onSave={handleSaveSection}
        section={editingSection}
      />

      <ItemFormModal
        isOpen={itemModal.isOpen}
        onClose={() => {
          itemModal.onClose();
          setEditingItem(null);
        }}
        onSave={handleSaveItem}
        item={editingItem}
        sectionId={activeSectionId}
      />

      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => {
          deleteModal.onClose();
          setDeleteTarget(null);
        }}
        onConfirm={handleConfirmDelete}
        target={deleteTarget}
      />
    </Box>
  );
};

export default AdminHealthTopicsLayout;
